import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';

export interface Module {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  icon: string;
  isCore: boolean;
  isEnabled: boolean;
  sortOrder?: number;
  settings?: Record<string, unknown>;
}

export interface CompanyModules {
  companyId: number;
  companyName: string;
  modules: Module[];
}

export const useModulesStore = defineStore('modules', () => {
  const modules = ref<Module[]>([]);
  const enabledModuleSlugs = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isLoaded = computed(() => modules.value.length > 0);
  /** Bumped by $reset (another user / company): an answer from before is dropped. */
  let generation = 0;

  const modulesByCategory = computed(() => {
    const grouped: Record<string, Module[]> = {};
    modules.value.forEach((m) => {
      if (!grouped[m.category]) {
        grouped[m.category] = [];
      }
      grouped[m.category].push(m);
    });
    return grouped;
  });

  function isModuleEnabled(slug: string): boolean {
    return enabledModuleSlugs.value.includes(slug);
  }

  async function fetchModules(): Promise<void> {
    loading.value = true;
    error.value = null;
    const gen = generation;
    try {
      const response = await api.get('/modules');
      if (gen !== generation) return;
      modules.value = response.data.data;
      enabledModuleSlugs.value = modules.value
        .filter((m) => m.isEnabled)
        .map((m) => m.slug);
    } catch (err) {
      error.value = 'Failed to load modules';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchEnabledModules(): Promise<void> {
    loading.value = true;
    error.value = null;
    const gen = generation;
    try {
      const response = await api.get('/modules/enabled');
      if (gen !== generation) return;
      enabledModuleSlugs.value = response.data.data;
    } catch (err) {
      error.value = 'Failed to load enabled modules';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function $reset(): void {
    generation++;
    modules.value = [];
    enabledModuleSlugs.value = [];
    loading.value = false;
    error.value = null;
  }

  return {
    modules,
    enabledModuleSlugs,
    loading,
    error,
    isLoaded,
    modulesByCategory,
    isModuleEnabled,
    fetchModules,
    fetchEnabledModules,
    $reset,
  };
});

// Super admin modules store for managing company modules
export const useSuperAdminModulesStore = defineStore('superAdminModules', () => {
  const allModules = ref<Module[]>([]);
  const companyModules = ref<CompanyModules | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);

  async function fetchAllModules(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get('/superadmin/modules');
      allModules.value = response.data.data;
    } catch (err) {
      error.value = 'Failed to load modules';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchCompanyModules(companyId: number): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get(`/superadmin/companies/${companyId}/modules`);
      companyModules.value = response.data.data;
    } catch (err) {
      error.value = 'Failed to load company modules';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCompanyModules(
    companyId: number,
    modules: { moduleId: number; isEnabled: boolean }[]
  ): Promise<void> {
    saving.value = true;
    error.value = null;
    try {
      await api.patch(`/superadmin/companies/${companyId}/modules`, { modules });

      // Update local state
      if (companyModules.value && companyModules.value.companyId === companyId) {
        const moduleUpdates = new Map(modules.map((m) => [m.moduleId, m.isEnabled]));
        companyModules.value.modules = companyModules.value.modules.map((m) => ({
          ...m,
          isEnabled: m.isCore || (moduleUpdates.has(m.id) ? moduleUpdates.get(m.id)! : m.isEnabled),
        }));
      }
    } catch (err) {
      error.value = 'Failed to update company modules';
      throw err;
    } finally {
      saving.value = false;
    }
  }

  async function toggleModule(companyId: number, moduleId: number, isEnabled: boolean): Promise<void> {
    saving.value = true;
    error.value = null;
    try {
      await api.patch(`/superadmin/companies/${companyId}/modules/${moduleId}`, { isEnabled });

      // Update local state
      if (companyModules.value && companyModules.value.companyId === companyId) {
        const moduleIndex = companyModules.value.modules.findIndex((m) => m.id === moduleId);
        if (moduleIndex !== -1 && !companyModules.value.modules[moduleIndex].isCore) {
          companyModules.value.modules[moduleIndex].isEnabled = isEnabled;
        }
      }
    } catch (err) {
      error.value = 'Failed to toggle module';
      throw err;
    } finally {
      saving.value = false;
    }
  }

  function $reset(): void {
    allModules.value = [];
    companyModules.value = null;
    loading.value = false;
    saving.value = false;
    error.value = null;
  }

  return {
    allModules,
    companyModules,
    loading,
    saving,
    error,
    fetchAllModules,
    fetchCompanyModules,
    updateCompanyModules,
    toggleModule,
    $reset,
  };
});
